import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 管理者権限チェック
    if (!user || user.user_type !== 'admin') {
      toast.error('管理者権限が必要です');
      navigate('/');
      return;
    }

    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [statsResponse, jobsResponse, appsResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/jobs`),
        axios.get(`${API}/admin/applications`)
      ]);

      setStats(statsResponse.data);
      setJobs(jobsResponse.data);
      setApplications(appsResponse.data);

    } catch (error) {
      toast.error('データの取得に失敗しました');
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // const calculateStats = ... (Deprecated: server-side stats used)

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'status-badge status-active', label: '募集中', icon: <CheckCircleIcon className="w-4 h-4" /> },
      filled: { class: 'status-badge status-completed', label: '募集終了', icon: <ClockIcon className="w-4 h-4" /> },
      cancelled: { class: 'status-badge status-rejected', label: 'キャンセル', icon: <XCircleIcon className="w-4 h-4" /> }
    };

    const badge = badges[status] || badges.active;
    return (
      <span className={`${badge.class} flex items-center`}>
        {badge.icon}
        <span className="ml-1">{badge.label}</span>
      </span>
    );
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('この求人を削除してもよろしいですか？')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`);
      toast.success('求人を削除しました');
      setJobs(jobs.filter(job => job.id !== jobId));
      // Refresh stats if needed, or update locally
      setStats(prev => ({ ...prev, totalJobs: prev.totalJobs - 1 }));
    } catch (error) {
      toast.error('削除に失敗しました');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">管理者ダッシュボード (Clean)</h1>
          <p className="text-gray-600">
            bytersプラットフォームの管理と監視
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BriefcaseIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総案件数</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalJobs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">アクティブ案件</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeJobs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総応募数</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalApplications || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">今週の新規案件</p>
                <p className="text-2xl font-bold text-gray-900">{stats.weekJobs || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'overview', label: 'ダッシュボード', icon: <ChartBarIcon className="w-5 h-5" /> },
                { key: 'jobs', label: 'お仕事管理', icon: <BriefcaseIcon className="w-5 h-5" /> },
                { key: 'users', label: 'ユーザー管理', icon: <UsersIcon className="w-5 h-5" /> },
                { key: 'user-withdrawals', label: '出金申請管理', icon: <BanknotesIcon className="w-5 h-5" /> },
                { key: 'identity-verifications', label: '本人確認', icon: <ShieldCheckIcon className="w-5 h-5" /> },
                { key: 'qualifications', label: '保有資格', icon: <AcademicCapIcon className="w-5 h-5" /> },
                { key: 'applications', label: '応募管理', icon: <DocumentTextIcon className="w-5 h-5" /> },
                { key: 'settings', label: '手数料設定', icon: <PencilIcon className="w-5 h-5" /> }

              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    if (tab.key === 'user-withdrawals') {
                      navigate('/admin/withdrawals');
                    } else if (tab.key === 'identity-verifications') {
                      navigate('/admin/identity-verifications');
                    } else if (tab.key === 'qualifications') {
                      navigate('/admin/qualifications');
                    } else if (tab.key === 'settings') {
                      navigate('/admin/settings');
                    } else {
                      setActiveTab(tab.key);
                    }
                  }}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">システム概要</h2>

                {/* 最近の活動 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">今日の統計</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">新規求人</span>
                        <span className="font-semibold">{stats.todayJobs}件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">アクティブ求人</span>
                        <span className="font-semibold">{stats.activeJobs}件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">総応募数</span>
                        <span className="font-semibold">{stats.totalApplications}件</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">月間統計</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">今月の新規求人</span>
                        <span className="font-semibold">{stats.monthJobs}件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">完了した求人</span>
                        <span className="font-semibold">{stats.completedJobs}件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">成功率</span>
                        <span className="font-semibold">
                          {stats.totalJobs ? Math.round((stats.completedJobs / stats.totalJobs) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'jobs' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">求人管理</h2>
                  <div className="text-sm text-gray-600">
                    総 {jobs.length} 件の求人
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          求人情報
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          企業
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ステータス
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          応募数
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          作成日
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {jobs.map((job) => (
                        <tr key={job.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{job.title}</div>
                              <div className="text-sm text-gray-500">{job.category}</div>
                              <div className="text-sm text-gray-500">{job.hourly_rate.toLocaleString()}/時</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{job.employer_name}</div>
                            <div className="text-sm text-gray-500">{job.location}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(job.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {job.current_applicants} / {job.max_applicants}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(job.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => navigate(`/jobs/${job.id}`)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => toast.info('編集機能は開発中です')}
                                className="text-green-600 hover:text-green-700"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteJob(job.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">ユーザー管理</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Link
                    to="/admin/job-seekers"
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center mb-4">
                      <UserGroupIcon className="w-8 h-8 text-blue-600 mr-3" />
                      <h3 className="text-xl font-medium text-gray-900">求職者管理</h3>
                    </div>
                    <p className="text-gray-600 mb-4">
                      登録済み求職者の情報管理、プロフィール確認、活動状況の監視
                    </p>
                    <div className="text-blue-600 font-medium">
                      管理画面へ移動
                    </div>
                  </Link>

                  <Link
                    to="/admin/employers"
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center mb-4">
                      <BuildingOfficeIcon className="w-8 h-8 text-purple-600 mr-3" />
                      <h3 className="text-xl font-medium text-gray-900">事業者管理</h3>
                    </div>
                    <p className="text-gray-600 mb-4">
                      事業者アカウントの管理、求人投稿状況、事業者認証の管理
                    </p>
                    <div className="text-purple-600 font-medium">
                      管理画面へ移動
                    </div>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">応募管理</h2>
                  <div className="text-sm text-gray-600">
                    総 {applications.length} 件の応募
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <ClockIcon className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">選考中</h3>
                    <p className="text-2xl font-bold text-yellow-600">
                      {applications.filter(a => a.status === 'pending').length}
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <CheckCircleIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">採用済み</h3>
                    <p className="text-2xl font-bold text-green-600">
                      {applications.filter(a => a.status === 'accepted').length}
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <XCircleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">不採用</h3>
                    <p className="text-2xl font-bold text-red-600">
                      {applications.filter(a => a.status === 'rejected').length}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">応募者</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">求人</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">応募日</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {applications.map((app) => (
                          <tr key={app.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{app.applicant_name}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{app.job_title}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                {app.status === 'accepted' ? '採用' : app.status === 'rejected' ? '不採用' : '選考中'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(app.applied_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;